// WhatsApp sticker helper: converts an image (PNG with transparency or URL)
// to the official WhatsApp sticker format: WebP 512×512 px, <100 KB static.
// Uses Canvas API client-side — zero IA cost.

/**
 * Load any image source (dataURL, blob URL, or remote URL) into an Image element.
 */
function loadImg(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
    img.src = src;
  });
}

/**
 * Build a WhatsApp-compatible sticker from an image source.
 * Returns a File. Default format is PNG (preserves transparency, accepted by WhatsApp and ALL share targets).
 * WebP is also supported for users that want max compression (~10× smaller).
 *
 * @param {string} imageSrc - dataURL, blobURL, or remote URL
 * @param {object} opts
 * @param {number} opts.padding - inner padding (0..0.2). Default 0.06
 * @param {number} opts.maxKB - target max size in KB. Default 95 (only for webp)
 * @param {'png'|'webp'} opts.format - Default 'png' for max compatibility
 * @returns {Promise<File>}
 */
export async function buildWhatsAppSticker(imageSrc, opts = {}) {
  const { padding = 0.06, maxKB = 95, format = 'png' } = opts;
  const SIZE = 512;

  const img = await loadImg(imageSrc);

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, SIZE, SIZE);

  const targetArea = SIZE * (1 - padding * 2);
  const ratio = img.width / img.height;
  let drawW;
  let drawH;
  if (ratio > 1) {
    drawW = targetArea;
    drawH = targetArea / ratio;
  } else {
    drawH = targetArea;
    drawW = targetArea * ratio;
  }
  const dx = (SIZE - drawW) / 2;
  const dy = (SIZE - drawH) / 2;
  ctx.drawImage(img, dx, dy, drawW, drawH);

  let blob = null;
  if (format === 'png') {
    blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  } else {
    const qualities = [0.92, 0.85, 0.75, 0.65, 0.55, 0.45, 0.35];
    for (const q of qualities) {
      blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', q));
      if (!blob) break;
      if (blob.size <= maxKB * 1024) break;
    }
  }
  if (!blob) throw new Error('No se pudo generar el sticker');

  const ext = format === 'png' ? 'png' : 'webp';
  const mime = format === 'png' ? 'image/png' : 'image/webp';
  return new File([blob], `sticker-${Date.now()}.${ext}`, { type: mime });
}

/**
 * Share a sticker via the device's native share menu (WhatsApp, Telegram, etc).
 * Falls back to download if the device doesn't support sharing files.
 *
 * @param {File} stickerFile
 * @param {string} title
 * @returns {Promise<'shared' | 'downloaded'>}
 */
export async function shareStickerOrDownload(stickerFile, title = 'Sticker PaliStore') {
  if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [stickerFile] })) {
    try {
      await navigator.share({ files: [stickerFile], title });
      return 'shared';
    } catch (err) {
      if (err.name === 'AbortError') return 'shared';
      // fall through to download
    }
  }
  // Fallback: trigger download
  const url = URL.createObjectURL(stickerFile);
  const link = document.createElement('a');
  link.href = url;
  link.download = stickerFile.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'downloaded';
}
